import React from 'react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import {
  useDeleteAuth0UserMutation,
  useDeleteFgaGroupMutation,
  useRemoveFgaUserMutation,
} from '@circuitry-ai/doc-data';
import { handleApiError, handleApiSuccess } from '../fga-utils';

export interface DeleteModalProps {
  readonly isOpen: boolean;
  readonly onOpenChange: (isOpen: boolean) => void;
  readonly type: 'group' | 'user';
  readonly data: {
    name: string;
    email?: string;
    user_id?: string;
    groups?: string[];
  };
  readonly onSuccess?: () => void;
}

export function DeleteModal({ isOpen, onOpenChange, type, data, onSuccess }: DeleteModalProps) {
  const [deleteFgaGroup, { isLoading: isDeletingGroup }] = useDeleteFgaGroupMutation();
  const [removeFgaUser, { isLoading: isRemovingUser }] = useRemoveFgaUserMutation();
  const [deleteAuth0User, { isLoading: isDeletingAuth0User }] = useDeleteAuth0UserMutation();
  const isLoading = isDeletingGroup || isRemovingUser || isDeletingAuth0User;

  const handleDelete = async () => {
    try {
      if (type === 'group') {
        await deleteFgaGroup({ group: data.name }).unwrap();
      } else {
        const auth0UserId = data.user_id?.trim();
        if (!auth0UserId) {
          throw new Error('User id is required to delete user');
        }

        const groups = (data.groups ?? []).filter((group) => group?.trim());
        for (const group of groups) {
          await removeFgaUser({ user_id: auth0UserId, group }).unwrap();
        }
        await deleteAuth0User(auth0UserId).unwrap();
      }

      handleApiSuccess(type === 'group' ? 'Group deleted successfully' : 'User deleted successfully');
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      handleApiError(error, `delete ${type}`);
    }
  };

  if (!isOpen) return null;

  const isGroup = type === 'group';
  const title = isGroup ? 'Delete Role' : 'Delete User';
  const description = isGroup 
    ? `Are you sure you want to delete the role "${data.name}" ? This will remove all associated users and their access permissions. This action cannot be undone.`
    : `Are you sure you want to delete the user "${data.name}" ? This will remove their access to all assigned roles. This action cannot be undone.`;

  return (
    <>
      <button 
        className="fixed inset-0 bg-black bg-opacity-50 z-[42]" 
        onClick={() => onOpenChange(false)} 
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-content1 rounded-lg shadow-xl w-full max-w-lg">
          <div className="px-4 pt-4">
            <h2 className="text-xl font-semibold text-foreground">{title}</h2>
          </div>

          <div className="px-4 py-2">
            <p className="text-default-600">
              {description}
            </p>
          </div>

          <div className="p-4">
            <div className="flex justify-end gap-3">
              <CtyButton
                variant="bordered"
                onPress={() => onOpenChange(false)}
                isDisabled={isLoading}
                className="bg-content1 border-default-300 text-default-600 hover:bg-default-100"
              >
                Cancel
              </CtyButton>
              <CtyButton
                color="danger"
                onPress={handleDelete}
                isLoading={isLoading}
                className="bg-danger text-white hover:bg-danger"
              >
                {title}
              </CtyButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default DeleteModal;
